import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';

interface RetriableSessionRequest extends InternalAxiosRequestConfig {
  _webSessionRetried?: boolean;
  _skipWebSessionRetry?: boolean;
}

interface SessionRetryDependencies {
  request: (config: RetriableSessionRequest) => Promise<unknown>;
  refresh: () => Promise<void>;
}

export const SESSION_TERMINATED_HEADER = 'x-session-terminated';

type SessionTerminationListener = (reason: string) => void;

const sessionTerminationListeners = new Set<SessionTerminationListener>();

/**
 * Subscribe to "the server just destroyed this session" notices. The signal
 * matters because the response that clears the cookies is often a plain 204
 * from `/api/user/me`, which is otherwise identical to an anonymous visitor's
 * 204 - the session check would only notice on its next minute-boundary poll.
 */
export function onSessionTerminated(
  listener: SessionTerminationListener,
): () => void {
  sessionTerminationListeners.add(listener);
  return () => {
    sessionTerminationListeners.delete(listener);
  };
}

type HeaderBag = Headers | Record<string, unknown> | undefined | null;

function headerValue(headers: HeaderBag): string | undefined {
  if (!headers) return undefined;
  if (typeof (headers as Headers).get === 'function') {
    return (headers as Headers).get(SESSION_TERMINATED_HEADER) ?? undefined;
  }
  const bag = headers as Record<string, unknown>;
  const key = Object.keys(bag).find(
    (name) => name.toLowerCase() === SESSION_TERMINATED_HEADER,
  );
  const value = key ? bag[key] : undefined;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Fan a termination notice out to subscribers, if the response carries one. */
export function noteSessionTermination(headers: HeaderBag): void {
  const reason = headerValue(headers);
  if (!reason) return;
  for (const listener of [...sessionTerminationListeners]) listener(reason);
}

function responseError(error: AxiosError): string | undefined {
  const data = error.response?.data;
  return typeof data === 'object' && data && 'error' in data
    ? String((data as { error?: unknown }).error)
    : undefined;
}

export function createSessionRetryHandler({
  request,
  refresh,
}: SessionRetryDependencies) {
  return async (error: AxiosError): Promise<unknown> => {
    const config = error.config as RetriableSessionRequest | undefined;
    if (!config || config._webSessionRetried || config._skipWebSessionRetry) {
      throw error;
    }

    noteSessionTermination(error.response?.headers as HeaderBag);
    const status = error.response?.status;
    const message = responseError(error);
    const retryableConflict =
      status === 409 && message === 'Refresh in progress';
    const recoverableUnauthorized =
      status === 401 && message === 'Authentication required';
    if (!retryableConflict && !recoverableUnauthorized) throw error;

    config._webSessionRetried = true;
    await refresh();
    return request(config);
  };
}

interface RefreshCoordinatorDependencies {
  fetch: typeof fetch;
  delay: (milliseconds: number) => Promise<void>;
  retryDelaysMs?: number[];
}

export function createRefreshCoordinator({
  fetch: fetchRequest,
  delay,
  retryDelaysMs = [50, 100, 200, 400, 800, 1600, 2000],
}: RefreshCoordinatorDependencies): () => Promise<void> {
  let refreshInFlight: Promise<void> | null = null;
  return () => {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      for (let attempt = 0; ; attempt += 1) {
        const response = await fetchRequest('/api/authentication/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (response.status === 204) return;
        if (response.status !== 409 || attempt >= retryDelaysMs.length) {
          throw new Error(`Session refresh failed (${response.status})`);
        }
        await delay(retryDelaysMs[attempt]);
      }
    })().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  };
}

const coordinateSessionRefresh = createRefreshCoordinator({
  fetch: (...args) => fetch(...args),
  delay: (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds)),
});

async function retryableFetchResponse(response: Response): Promise<boolean> {
  if (response.status !== 401 && response.status !== 409) return false;
  try {
    const data = (await response.clone().json()) as { error?: unknown };
    return (
      (response.status === 401 && data.error === 'Authentication required') ||
      (response.status === 409 && data.error === 'Refresh in progress')
    );
  } catch {
    return false;
  }
}

/** Authenticated fetch with the same refresh single-flight as Axios clients. */
export async function sessionFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);
  noteSessionTermination(response.headers);
  if (!(await retryableFetchResponse(response))) return response;
  await coordinateSessionRefresh();
  const retried = await fetch(input, init);
  noteSessionTermination(retried.headers);
  return retried;
}

let installed = false;

export function installSessionRetryInterceptor(
  client: AxiosInstance = axios,
): void {
  if (installed) return;
  installed = true;
  client.interceptors.response.use(
    (response) => {
      noteSessionTermination(response.headers as HeaderBag);
      return response;
    },
    createSessionRetryHandler({
      request: (config) => client.request(config),
      refresh: coordinateSessionRefresh,
    }),
  );
}
